// プロローグ（初回起動の流れ）：「三百年前――」の数行（ADV） → 酒呑童子の最終戦（battle?prologue=1）→ 天下統一 →「三百年後――」
// → 名前（ゲストで未設定なら）→ 本編の序章（屋根修復・四人の覚醒）→ 現代の初戦。
// 序章は通常の物語再生に任せ、未読の場面を既読にしない。物語帳・メモと同じ台詞を使う。
import { PROLOGUE_STAGE, randomName } from '@hyakki/core';
import { api, track } from '../api';
import type { StoryBgm } from '@hyakki/core/data/stories';
import { bgm, prepareBgm, storyBgmKey } from '../audio';
import { go, register } from '../router';
import { onLoggedIn } from '../session';
import { refreshMe, state } from '../state';
import { markSeen, seen } from '../tutorial';
import { actorHtml, actorPortrait, speakerName, storyBackground } from '../story/actors';
import { openSkipDialog } from '../story/skip';
import { STORY_SUMMARIES } from '../story/summaries';
import { icon } from '../ui/icons';
import { toast } from '../ui/overlay';
import { errorText } from '../ui/status';
import { avatarChoicesHtml, avatarImageUrls } from '../ui/avatar';
import { storyImages, warmImages, warmStage, warmStageById } from '../preload';
import { readLocal, writeLocal } from '../story/storage';
import { loadSaved } from '../battle/controller';

/** ADV の1行。title は黒地の大きな字幕（「三百年前――」など）。bg・cast・place は変えるときだけ書く（前の行を引き継ぐ） */
interface Beat {
  who?: 'narrator' | 'player' | 'shuten' | 'akari';
  text: string;
  title?: boolean;
  bg?: string;
  place?: string;
  cast?: string[];
  /** 背景の色味（夜・地下の火）。変えるときだけ書く。'' で元に戻す */
  mood?: 'night' | 'fire' | '';
  /** 画面を揺らす（地震）。big は強く長く */
  shake?: 'small' | 'big';
  /** この行の前に暗転して間を置く（ミリ秒）。間の途中は押しても進まない */
  pause?: number;
  /** 曲を変える（ストーリーの曲の種類）。変えるときだけ書く */
  music?: StoryBgm;
}
// 酒呑童子の立ち絵は物語の e100（三百年後は敵として出る同じ人物）
const ACTOR: Record<string, string> = { shuten: 'e100' };

const INTRO: Beat[] = [
  { text: '三百年前――', title: true, music: 'mystery' },
  { text: '妖と人が、この国を二つに分けて争っていた。', bg: 'capital', place: '人の世の最後の城', cast: [] },
  { text: '百鬼を率いる王と、その右腕・酒呑童子は、人の世の最後の城を前にしていた。', cast: ['shuten'] },
];
const AFTER: Beat[] = [
  // 王が消えた夜（本編10章の真相の前振り。力を引き戻したこと・その後のことは10章まで伏せる）
  { music: 'warm', text: '最後の城が落ちた夜、百鬼は勝利の酒に酔った。', bg: 'capital', place: '人の世の最後の城・宴の広間', cast: ['shuten'], mood: 'night' },
  { text: '酒呑が、あなたの前にも杯を置く。' },
  { who: 'shuten', text: '一杯くらい付き合え、大将。' },
  { who: 'player', text: 'ああ。' },
  { text: '手を伸ばした途端、杯が倒れた。梁が軋む。', shake: 'small', music: 'tense' },
  { who: 'shuten', text: '地震か……！？' },
  { text: '床に走った亀裂から、熱気が顔を打った。', shake: 'big' },
  { text: 'あなたは地下へ駆け下りた。酒呑が後を追う。', place: '人の世の最後の城・地下', mood: 'fire' },
  { text: '裂けた地脈から、都へ向けて火がせり上がる。' },
  { who: 'shuten', text: '上には、まだ大勢いるぞ。' },
  { who: 'player', text: '裂け目はここで塞ぐ。' },
  { text: 'あなたは酒呑の胸を押し、扉の外へ出した。' },
  { who: 'shuten', text: '待て！　一人で背負うな、俺も残る！' },
  { text: 'あなたは一人、内側から戸を閉めた。', cast: [], music: 'sad' },
  { who: 'player', text: 'すぐ戻る。玉座は預けた。' },
  { pause: 1800, text: 'しかし、夜が明けても、酒呑は扉の前を離れなかった。', cast: ['shuten'], mood: 'night' },
  { text: '王は戻らず、勝利の宴が再び開かれることもなかった。' },
  { text: '三百年後――', title: true },

];

/**
 * 名前の入力まで進んだことをこの端末に覚える（オートセーブ）。
 * 名前の登録前はサーバーに保存できない（requireName）ので、アプリを閉じて開き直しても名前の入力から続けられるように手元に持つ
 */
const nameKey = () => `hk.prologue.name.${state.me?.user.id ?? 'guest'}`;
const reachedName = () => readLocal<number>(nameKey(), 0) === 1;
/** 初回の流れの途中で名前の入力まで来ている（戦いと寸劇は済み、名前が未登録） */
export const prologueAtName = () => !!state.me?.user.needsName && reachedName();

/** 初回の流れに入るべきか（プロローグを見ておらず、まだ1面も勝っていない） */
// 見たことは手元（seen）でも数える：サーバーへの保存より先に refreshMe が古いプロフィールを読んでも、プロローグへ戻さない
export const needsPrologue = () => !!state.profile && !seen('prologue') && !reachedName() && !Object.values(state.profile.progress ?? {}).some((v) => v.clears > 0);

register('prologue', {
  title: 'プロローグ',
  full: true,
  nav: 'top',
  mount(el, params) {
    const step = params.get('step') ?? 'intro';
    if (step === 'battle') return void go('battle', { prologue: 1 }, true);
    // 名前の入力まで進んでいた（開き直し）：寸劇をもう一度見せず、名前から
    if (step === 'name' || prologueAtName()) return nameStep(el);
    // 戦いの途中で閉じていた：序章を繰り返さず、その戦いの続きから（退避は battle/controller が端末に持つ）
    if (step !== 'after') {
      const s = loadSaved();
      if (s?.setup.stageId === PROLOGUE_STAGE && s.commands.length) return void go('battle', { prologue: 1, resume: 1 }, true);
    }
    el.innerHTML = `<article class="story-reader prologue-adv">
      <div class="story-stage"><div class="story-location"></div><div class="story-cast" aria-hidden="true"></div></div>
      <div class="story-dialogue-wrap"><button class="story-btn story-skip">スキップ <b>»</b></button><div class="story-speaker"></div><button class="story-dialogue" aria-label="次へ"><span class="story-line-text" aria-live="polite"></span><span class="story-tap" aria-hidden="true">タップで次へ <b>▸</b></span></button></div>
      <div class="pl-title" hidden><p class="pl-line title"></p></div>
      <div class="pl-gap" hidden></div></article>`;
    const $ = <T extends HTMLElement>(q: string) => el.querySelector<T>(q)!;
    const beats = step === 'after' ? AFTER : INTRO;
    if (step !== 'after') track('prologue.intro');
    // 曲は最初に指定のある行の曲から（途中の行の曲はその行で切り替える）
    void bgm(storyBgmKey(beats.find((b) => b.music)?.music ?? 'mystery'));
    if (step !== 'after') prepareBgm('boss2'); // 続く酒呑の最終戦
    // 次の場面の絵を裏で読む：三百年前 → 最終戦の盤 → 三百年後の宴 → 名前と姿 → 序章 → 里の初戦（今の画面の絵を先に読ませるため少し待つ）
    window.setTimeout(() => {
      const images = (list: Beat[]) => list.flatMap((b) => [b.bg ? storyBackground(b.bg) : undefined, ...(b.cast ?? []).map((id) => actorPortrait(ACTOR[id] ?? id))]);
      if (step !== 'after') warmStage(['h01', 'h09', 'h00'], 'capital');
      warmImages([...images(AFTER), ...avatarImageUrls(), ...storyImages('main-00')]);
      warmStageById(1);
    }, 1200);
    let i = -1;
    let shownAt = -Infinity;
    let bg = '', place = '';
    let cast: string[] = [];
    const finish = () => (step === 'after' ? toPresent() : go('battle', { prologue: 1 }, true));
    let blockUntil = 0;
    const next = () => {
      if (performance.now() - shownAt < 350 || performance.now() < blockUntil) return;
      i++;
      if (i >= beats.length) return void finish();
      const b = beats[i];
      shownAt = performance.now();
      if (b.pause) {
        // 暗転して間を置き、明けてからこの行を出す
        const gap = $('.pl-gap');
        gap.hidden = false;
        gap.classList.remove('out');
        blockUntil = performance.now() + b.pause;
        window.setTimeout(() => {
          show(b);
          gap.classList.add('out');
          window.setTimeout(() => (gap.hidden = true), 700);
        }, b.pause);
        return;
      }
      show(b);
    };
    const show = (b: Beat) => {
      if (b.music) void bgm(storyBgmKey(b.music));
      const title = $('.pl-title');
      // 字幕（黒地）の間はスキップを隠す（字幕に覆われて押せないので、見せない）
      $('.story-skip').hidden = !!b.title;
      if (b.title) {
        title.hidden = false;
        title.firstElementChild!.textContent = b.text;
        return;
      }
      title.hidden = true;
      if (b.bg && b.bg !== bg) {
        bg = b.bg;
        $('.story-reader').style.setProperty('--story-bg', `url('${storyBackground(bg)}')`);
      }
      if (b.place) place = b.place;
      if (b.mood != null) $('.story-reader').dataset.mood = b.mood;
      if (b.shake && !state.settings.reduceShake) {
        const r = $('.story-reader');
        r.classList.remove('shake-small', 'shake-big');
        void r.offsetWidth;
        r.classList.add(`shake-${b.shake}`);
      }
      if (b.cast) cast = b.cast;
      const who = b.who ?? 'narrator';
      $('.story-location').textContent = place;
      $('.story-cast').innerHTML = cast.map((id) => actorHtml(ACTOR[id] ?? id, id === who || who === 'narrator')).join('');
      $('.story-speaker').textContent = who === 'narrator' ? '' : who === 'shuten' ? '酒呑童子' : speakerName(who, state.me?.user);
      $('.story-speaker').dataset.speaker = who;
      $('.story-dialogue-wrap').classList.toggle('is-narration', who === 'narrator');
      $('.story-line-text').textContent = b.text;
    };
    let leaving = false;
    const toPresent = async () => {
      // 最後の行の連打で2回進まない（名前の入力が描き直されて消える・戦闘へ2回進む）
      if (leaving) return;
      leaving = true;
      // 初戦前の序章は battle の通常導線で再生する（屋根修復と覚醒を含む）。
      markSeen('prologue');
      track('prologue.done');
      // 名前の登録で refreshMe するので、見たことをサーバーへ保存し終えてから進む
      await api.tutorial(['prologue']).catch(() => undefined);
      if (state.me?.user.needsName) {
        writeLocal(nameKey(), 1);
        return nameStep(el);
      }
      await onLoggedIn({ skipBonus: true });
      go('battle', { stage: 1, char: 'akari' }, true);
    };
    // スキップ：物語の再生と同じく、この場面のあらすじを見せてから確かめる。✓ で先へ、× で続きを読む
    let closeSkip: (() => void) | undefined;
    const openSkip = () => {
      if (leaving || closeSkip) return;
      const summary = STORY_SUMMARIES[`prologue:${step}`];
      if (!summary) return void finish();
      closeSkip = openSkipDialog({ eyebrow: 'プロローグ', title: step === 'after' ? '王が消えた夜' : '三百年前', summary, skip: finish, closed: () => (closeSkip = undefined) });
    };
    el.querySelector('.prologue-adv')!.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('.story-skip')) return void openSkip();
      next();
    });
    next();
    return () => closeSkip?.();
  },
});

/** 名前（ゲストで未設定のとき）：物語の流れで聞く */
function nameStep(el: HTMLElement) {
  el.innerHTML = `<div class="prologue"><form class="pl-name">
    <p class="pl-line narrator">紅葉の里で育った。幼なじみが呼ぶ。</p>
    <label><span>あなたの名は</span><span class="pl-name-row"><input name="name" maxlength="12" minlength="2" required placeholder="例：あるじ" aria-label="名前"><button type="button" class="btn pl-roll" data-roll>${icon('dice')}ランダム</button></span></label>
    <small class="muted">チャットと番付に表示されます（2〜12文字）</small>
    ${avatarChoicesHtml()}
    <button class="btn primary wide">決める</button></form></div>`;
  let busy = false;
  // 名前の候補（data/random-names.ts）から1つ入れる。押すたびに別の名前
  el.querySelector('[data-roll]')!.addEventListener('click', () => {
    const input = el.querySelector<HTMLInputElement>('input[name="name"]')!;
    input.value = randomName(input.value);
  });
  el.querySelector('form')!.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (busy) return;
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const name = String(data.get('name') ?? '').trim();
    if (!name) return;
    busy = true;
    const controls = form.querySelectorAll<HTMLInputElement | HTMLButtonElement>('input, button');
    controls.forEach(c => c.disabled = true);
    try {
      if (state.me?.user.needsName) await api.setName(name, String(data.get('avatar')));
      await refreshMe();
      // 名前登録前は保存できないため、登録後にプロローグ既読を確定する。
      await api.tutorial(['prologue']);
      await refreshMe();
      try {
        localStorage.removeItem(nameKey());
      } catch {
        /* 残っても needsName が false なら使わない */
      }
      await onLoggedIn({ skipBonus: true });
      go('battle', { stage: 1, char: 'akari' }, true);
    } catch (err) {
      toast(errorText(err), 'error', 4000);
    } finally {
      busy = false;
      controls.forEach(c => c.disabled = false);
    }
  });
  el.querySelector<HTMLInputElement>('input')?.focus();
}
